import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    LayoutDashboard,
    BookOpen,
    HelpCircle,
    GitBranch,
    UserCheck,
    Calendar,
    ClipboardList,
    Award,
    AlertTriangle,
    Settings,
    BarChart3
} from 'lucide-react';
import ModuleLayout from '../../layouts/ModuleLayout';
import Drawer from '../../components/common/Drawer';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

// Access Denied Component
const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

// Import all training components (Reusing existing production components)
import {
    TrainingDashboard,
    TrainingCatalog,
    ModulesLessons,
    QuizQuestionBank,
    LearningPaths,
    AssignmentsEnrollment,
    SessionsScheduling,
    AttendanceFeedback,
    CertificatesCredits,
    ReportsAnalytics,
} from './training-development';

const TrainingModule = ({ initialTab: propTab, initialAction: propAction, initialActionId: propId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Configuration - EXACT labels from production UI
    const TRAINING_TABS = [
        { id: 'trainingDashboard', label: 'Learning Dashboard', icon: LayoutDashboard, permission: 'training' },
        { id: 'trainingCatalog', label: 'Learning Catalog', icon: BookOpen, permission: 'training' },
        { id: 'quizQuestionBank', label: 'Assessments & Question Bank', icon: HelpCircle, permission: 'training' },
        { id: 'learningPaths', label: 'Learning Paths', icon: GitBranch, permission: 'training' },
        { id: 'assignmentsEnrollment', label: 'Learning Assignments', icon: UserCheck, permission: 'training' },
        { id: 'sessionsScheduling', label: 'Session Schedules', icon: Calendar, permission: 'training' },
        { id: 'attendanceManagement', label: 'Session Attendance', icon: ClipboardList, permission: 'training' },
        { id: 'certificatesTranscripts', label: 'Certificates', icon: Award, permission: 'training' },
        { id: 'overviewAnalytics', label: 'Learning Analytics', icon: BarChart3, permission: 'training' },
    ];

    // Map legacy paths to tabs
    const getTabFromPath = useCallback((path) => {
        if (path === '/hrms/training') return null; // Use current param or default
        if (path.includes('/training_dashboard')) return 'trainingDashboard';
        if (path.includes('/training_catalog')) return 'trainingCatalog';
        if (path.includes('/modules_lessons')) return 'modulesLessons';
        if (path.includes('/quiz_question_bank')) return 'quizQuestionBank';
        if (path.includes('/learning_paths')) return 'learningPaths';
        if (path.includes('/assignments_enrollment')) return 'assignmentsEnrollment';
        if (path.includes('/sessions_scheduling')) return 'sessionsScheduling';
        if (path.includes('/attendance_feedback') || path.includes('/attendance_management')) return 'attendanceManagement';
        if (path.includes('/certificates_credits') || path.includes('/certificates_transcripts') || path.includes('/certificates')) return 'certificatesTranscripts';
        if (path.includes('/compliance_renewals') || path.includes('/compliance_expirations')) return 'complianceExpirations';
        if (path.includes('/reports_analytics') || path.includes('/training_analytics') || path.includes('/overview_analytics')) return 'overviewAnalytics';
        return null;
    }, []);

    // Map legacy paths to drawer actions
    const getDrawerActionFromPath = useCallback((path) => {
        // Training detail routes
        if (path.includes('/training_detail/')) {
            const id = path.split('/training_detail/')[1]?.split('/')[0];
            if (path.endsWith('/edit')) return { action: 'editTraining', id };
            return { action: 'viewTraining', id };
        }
        // Quiz detail routes
        if (path.includes('/quiz_detail/')) {
            const id = path.split('/quiz_detail/')[1]?.split('/')[0];
            if (path.endsWith('/edit')) return { action: 'editQuiz', id };
            return { action: 'viewQuiz', id };
        }
        // Learning path detail routes
        if (path.includes('/learning_path_detail/')) {
            const id = path.split('/learning_path_detail/')[1]?.split('/')[0];
            if (path.endsWith('/edit')) return { action: 'editLearningPath', id };
            return { action: 'viewLearningPath', id };
        }
        // Session detail routes
        if (path.includes('/session_detail/')) {
            const id = path.split('/session_detail/')[1]?.split('/')[0];
            return { action: 'viewSession', id };
        }
        // Certificate detail routes
        if (path.includes('/certificate_detail/')) {
            const id = path.split('/certificate_detail/')[1]?.split('/')[0];
            return { action: 'viewCertificate', id };
        }
        // Compliance detail routes
        if (path.includes('/compliance_item_detail/')) {
            const id = path.split('/compliance_item_detail/')[1]?.split('/')[0];
            return { action: 'viewComplianceItem', id };
        }
        return null;
    }, []);

    // Sync state from path/params
    useEffect(() => {
        const tabFromPath = getTabFromPath(location.pathname);
        const drawerFromPath = getDrawerActionFromPath(location.pathname);

        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        // Priority 1: Props (from HRMSContainer)
        if (propTab && !searchParams.get('tab')) {
            newParams.set('tab', propTab);
            changed = true;
        }

        // Priority 2: Path mapping (for legacy routes)
        if (tabFromPath && !searchParams.get('tab')) {
            newParams.set('tab', tabFromPath);
            changed = true;
        }

        if (drawerFromPath) {
            if (searchParams.get('action') !== drawerFromPath.action) {
                newParams.set('action', drawerFromPath.action);
                changed = true;
            }
            if (drawerFromPath.id && searchParams.get('id') !== drawerFromPath.id) {
                newParams.set('id', drawerFromPath.id);
                changed = true;
            }
        }

        // Apply defaults if still missing
        if (!newParams.get('tab') && !tabFromPath && !propTab) {
            newParams.set('tab', 'trainingDashboard');
            changed = true;
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [location.pathname, searchParams, setSearchParams, propTab, getTabFromPath, getDrawerActionFromPath]);

    // Derived state
    const activeTab = searchParams.get('tab') || 'trainingDashboard';
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');

    const handleTabChange = (tabId) => {
        setSearchParams({ tab: tabId });
    };

    const closeDrawer = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'trainingDashboard': return <TrainingDashboard />;
            case 'trainingCatalog': return <TrainingCatalog />;
            case 'modulesLessons': return <ModulesLessons />;
            case 'quizQuestionBank': return <QuizQuestionBank />;
            case 'learningPaths': return <LearningPaths />;
            case 'assignmentsEnrollment': return <AssignmentsEnrollment />;
            case 'sessionsScheduling': return <SessionsScheduling />;
            case 'attendanceManagement': return <AttendanceFeedback />;
            case 'certificatesTranscripts': return <CertificatesCredits />;
            case 'overviewAnalytics': return <ReportsAnalytics />;
            default: return <TrainingDashboard />;
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'createTraining': return 'Create Course';
            case 'viewTraining': return 'Course Details';
            case 'editTraining': return 'Edit Course';
            case 'createQuiz': return 'Create Assessment';
            case 'viewQuiz': return 'Assessment Details';
            case 'editQuiz': return 'Edit Assessment';
            case 'createLearningPath': return 'Create Learning Path';
            case 'viewLearningPath': return 'Learning Path Details';
            case 'editLearningPath': return 'Edit Learning Path';
            case 'enrollUsers': return 'Enroll Users';
            case 'viewSession': return 'Class Schedule Details';
            case 'markAttendance': return 'Mark Class Attendance';
            case 'viewCertificate': return 'Certificate Details';
            case 'viewComplianceItem': return 'Compliance Item Details';
            default: return '';
        }
    };

    const renderDrawerContent = () => {
        if (!action) return null;
        switch (action) {
            case 'createTraining': return <TrainingCatalog initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'viewTraining': return <TrainingCatalog initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'editTraining': return <TrainingCatalog initialMode="edit" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'createQuiz': return <QuizQuestionBank initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'viewQuiz': return <QuizQuestionBank initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'editQuiz': return <QuizQuestionBank initialMode="edit" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'createLearningPath': return <LearningPaths initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'viewLearningPath': return <LearningPaths initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'editLearningPath': return <LearningPaths initialMode="edit" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'enrollUsers': return <AssignmentsEnrollment initialMode="enroll" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'viewSession': return <SessionsScheduling initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'markAttendance': return <AttendanceFeedback initialMode="mark" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'viewCertificate': return <CertificatesCredits initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            default: return null;
        }
    };

    const isFullScreenDrawer = ['createTraining', 'editTraining', 'createQuiz', 'editQuiz', 'createLearningPath', 'editLearningPath', 'markAttendance'].includes(action);

    return (
        <PermissionGate permission="training" action="read" showFallback fallback={<AccessDenied />}>
            <ModuleLayout
                title="Learning & Development"
                tabs={TRAINING_TABS}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            >
                {renderTabContent()}
            </ModuleLayout>

            <Drawer
                isOpen={!!action}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                size={isFullScreenDrawer ? 'full' : 'xl'}
            >
                {renderDrawerContent()}
            </Drawer>
        </PermissionGate>
    );
};

export default TrainingModule;
